import Api from "@ciq/shared/api/api"
import eventBus from "@ciq/shared/hooks/eventBus"

/**
 * @typedef {Object} DatasourceMappingResponse
 * @property {Array<Object>} datasets - Array of configured dataset mappings
 * @property {Array<string>} groups - Array of available mapping group names
 * @property {Array<Object>} sources - Array of available datasource schemas
 */

/**
 * Fetch all datasource mappings from the API.
 *
 * @param {string} schoolId - The school ID
 * @param {AbortSignal} signal - AbortController signal for cancellation
 * @returns {Promise<DatasourceMappingResponse>} The datasource mapping data
 */
export async function fetchMappings(schoolId, signal) {
  const response = await Api.get("settings/datasource", {
    signal,
    params: { school_id: schoolId },
  })
  return response.data
}

/**
 * Create a new datasource mapping.
 *
 * @param {string} schoolId - The school ID
 * @param {Object} data - The new mapping data
 * @param {string} data.name - Name of the mapping
 * @param {string} data.datasource_id - ID of the datasource
 * @param {string} data.table_id - ID of the table
 * @param {AbortSignal} signal - AbortController signal for cancellation
 * @returns {Promise<DatasourceMappingResponse>} Updated datasource mapping data
 */
export async function createMapping(schoolId, data, signal) {
  const response = await Api.post("settings/datasource", data, {
    signal,
    params: { school_id: schoolId },
  })

  console.debug("adding datasource")
  eventBus.emit("athena-datasource-updated", {})

  return response.data
}

/**
 * Update an existing datasource mapping.
 *
 * @param {string} schoolId - The school ID
 * @param {string} datasourceId - The datasource ID to update
 * @param {Object} data - The updated mapping data
 * @param {AbortSignal} signal - AbortController signal for cancellation
 * @returns {Promise<DatasourceMappingResponse>} Updated datasource mapping data
 */
export async function updateMapping(schoolId, datasourceId, data, signal) {
  const response = await Api.put(`settings/datasource/${datasourceId}`, data, {
    signal,
    params: { school_id: schoolId },
  })

  console.debug(`updating datasource '${datasourceId}'`)
  eventBus.emit("athena-datasource-updated", {})

  return response.data
}

/**
 * Delete a datasource mapping.
 *
 * @param {string} schoolId - The school ID
 * @param {string} datasourceId - The datasource ID to delete
 * @param {AbortSignal} signal - AbortController signal for cancellation
 * @returns {Promise<DatasourceMappingResponse>} Updated datasource mapping data
 */
export async function deleteMapping(schoolId, datasourceId, signal) {
  const response = await Api.delete(`settings/datasource/${datasourceId}`, {
    signal,
    params: { school_id: schoolId },
  })

  console.debug(`deleting datasource '${datasourceId}'`)
  eventBus.emit("athena-datasource-updated", {})

  return response.data
}

/**
 * Add a new column mapping to a datasource.
 *
 * @param {string} schoolId - The school ID
 * @param {string} datasourceId - The datasource ID
 * @param {Object} data - The column mapping data
 * @param {string} data.column_id - Source column ID
 * @param {string} data.display_name - Display name for the column
 * @param {string} data.group - Group name
 * @param {string} data.format_options - Format options
 * @param {boolean} data.enabled - Whether the column is enabled
 * @param {boolean} data.map_enabled - Whether map functionality is enabled
 * @param {AbortSignal} signal - AbortController signal for cancellation
 * @returns {Promise<DatasourceMappingResponse>} Updated datasource mapping data
 */
export async function createColumn(schoolId, datasourceId, data, signal) {
  const response = await Api.post(`settings/datasource/${datasourceId}/column`, data, {
    signal,
    params: { school_id: schoolId },
  })

  console.debug(`adding column to datasource '${datasourceId}'`)
  eventBus.emit("athena-datasource-updated", {})

  return response.data
}

/**
 * Update an existing column mapping.
 *
 * @param {string} schoolId - The school ID
 * @param {string} datasourceId - The datasource ID
 * @param {string} columnId - The column mapping ID to update
 * @param {Object} data - The updated column data
 * @param {AbortSignal} signal - AbortController signal for cancellation
 * @returns {Promise<DatasourceMappingResponse>} Updated datasource mapping data
 */
export async function updateColumn(schoolId, datasourceId, columnId, data, signal) {
  const response = await Api.put(`settings/datasource/${datasourceId}/column/${columnId}`, data, {
    signal,
    params: { school_id: schoolId },
  })

  console.debug(`updating the datasource '${datasourceId}' column '${columnId}'`)
  eventBus.emit("athena-datasource-updated", {})

  return response.data
}

/**
 * Delete a column mapping.
 *
 * @param {string} schoolId - The school ID
 * @param {string} datasourceId - The datasource ID
 * @param {string} columnId - The column mapping ID to delete
 * @param {AbortSignal} signal - AbortController signal for cancellation
 * @returns {Promise<DatasourceMappingResponse>} Updated datasource mapping data
 */
export async function deleteColumn(schoolId, datasourceId, columnId, signal) {
  const response = await Api.delete(`settings/datasource/${datasourceId}/column/${columnId}`, {
    signal,
    params: { school_id: schoolId },
  })

  console.debug(`deleting datasource '${datasourceId}' column '${columnId}'`)
  eventBus.emit("athena-datasource-updated", {})

  return response.data
}

/**
 * Copy a dataset mapping to a new target table.
 *
 * @param {string} schoolId - The school ID
 * @param {string} datasetId - The source dataset ID to copy from
 * @param {string} targetTableId - The target table ID to copy mappings to
 * @param {AbortSignal} signal - AbortController signal for cancellation
 * @returns {Promise<Object>} The copy result with new_dataset_id and counts
 */
export async function copyDataset(schoolId, datasetId, targetTableId, signal) {
  const response = await Api.post(`settings/datasource/${datasetId}/copy`, { target_table_id: targetTableId }, {
    signal,
    params: { school_id: schoolId },
  })

  console.debug(`copied dataset '${datasetId}' to table '${targetTableId}'`)
  eventBus.emit("athena-datasource-updated", {})

  return response.data
}
